import { useCallback, useEffect, useRef, useState } from 'react';
import { cameraBearing } from '../../utils/camera/heading';

/*
 * The back camera's compass bearing while `active` (utils/camera/heading.js),
 * for tagging each photograph with the way it faced (migration 0090).
 *
 * Orientation events come many times a second, so the latest reading is kept
 * in a ref, not in state: nothing re-renders for them. read() gives the
 * bearing if it is fresh (MAX_AGE_MS), else null.
 *
 * iOS asks the person before it gives orientation, and only from a tap:
 * request() is called from one (Field Mode's Snap), once. Until then, and if
 * they decline, read() is null and photographs carry no bearing; nothing
 * else changes. state: 'idle' | 'live' | 'denied' | 'unsupported'.
 */
export const MAX_AGE_MS = 2000;

export function useDeviceHeading(active) {
  const latest = useRef({ bearing: null, at: 0 });
  const [state, setState] = useState(() => {
    const Orientation = typeof window !== 'undefined' ? window.DeviceOrientationEvent : undefined;
    if (!Orientation) return 'unsupported';
    return typeof Orientation.requestPermission === 'function' ? 'idle' : 'live';
  });

  useEffect(() => {
    if (!active || state !== 'live') return undefined;
    const on = (event) => {
      const bearing = cameraBearing(event);
      if (bearing !== null) latest.current = { bearing, at: Date.now() };
    };
    window.addEventListener('deviceorientationabsolute', on);
    window.addEventListener('deviceorientation', on);
    return () => {
      window.removeEventListener('deviceorientationabsolute', on);
      window.removeEventListener('deviceorientation', on);
    };
  }, [active, state]);

  const request = useCallback(async () => {
    if (state !== 'idle') return;
    try {
      const answer = await window.DeviceOrientationEvent.requestPermission();
      setState(answer === 'granted' ? 'live' : 'denied');
    } catch {
      setState('denied');
    }
  }, [state]);

  const read = useCallback((now = Date.now()) => {
    const { bearing, at } = latest.current;
    return bearing !== null && now - at <= MAX_AGE_MS ? bearing : null;
  }, []);

  return { state, request, read };
}
